import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Trash2, Upload } from "lucide-react";
import { crm } from "./crmApi";
import { uploadPhotos } from "@/services/leadsService";
import { API } from "@/services/apiClient";
import { Pill, Sel, Loading, Err } from "./ui";
import { BeforeAfter } from "@/components/common/BeforeAfter";
import { Button } from "@/components/ui/Button";

const CATEGORIES = ["Before", "During", "After", "Other"];

// Photo management: uploads go to website storage, the CRM pulls a copy into
// the matching Google Drive customer/job folder keyed by IDs.
export const PhotosTab = () => {
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [jobId, setJobId] = useState("");
  const [category, setCategory] = useState("Before");
  const [photos, setPhotos] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [compare, setCompare] = useState(null); // {before, after}

  useEffect(() => {
    crm("listCustomers").then(r => setCustomers(r.customers)).catch(() => {});
    crm("listJobs").then(r => setJobs(r.jobs)).catch(() => {});
  }, []);

  const load = async () => {
    if (!customerId) { setPhotos([]); return; }
    setError(null);
    try { const r = await crm("listPhotos", { customerId, jobId: jobId || undefined }); setPhotos(r.photos.reverse()); }
    catch (e) { setError(e); setPhotos([]); }
  };
  useEffect(() => { load(); }, [customerId, jobId]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !customerId) return;
    setUploading(true);
    try {
      const uploaded = await uploadPhotos(files.slice(0, 5));
      for (const u of uploaded) {
        await crm("attachPhoto", { customerId, jobId: jobId || undefined, category, url: `${API}/files/${u.path}` });
      }
      toast.success(`${uploaded.length} photo(s) filed to Drive as "${category}"`);
      load();
    } catch (err) { toast.error(err.message); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const remove = async (mediaId) => {
    try { await crm("deletePhoto", { mediaId }); toast.success("Photo deleted"); load(); }
    catch (err) { toast.error(err.message); }
  };

  const befores = (photos || []).filter(p => p["Category"] === "Before");
  const afters = (photos || []).filter(p => p["Category"] === "After");

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-4 gap-3">
        <Sel label="Customer" options={[{ value: "", label: "Select customer" }, ...customers.map(c => ({ value: c["Customer ID"], label: `${c["First Name"]} ${c["Last Name"]}`.trim() || c["Customer ID"] }))]}
          value={customerId} onChange={e => { setCustomerId(e.target.value); setJobId(""); }} />
        <Sel label="Job (optional)" options={[{ value: "", label: "All / lead photos" }, ...jobs.filter(j => j["Customer ID"] === customerId).map(j => ({ value: j["Job ID"], label: `${j["Job ID"]} — ${j["Job Title"]}` }))]}
          value={jobId} onChange={e => setJobId(e.target.value)} />
        <Sel label="Upload As" options={CATEGORIES} value={category} onChange={e => setCategory(e.target.value)} />
        <label className="block">
          <span className="block text-white/50 text-xs uppercase tracking-wider mb-1">Add Photos</span>
          <span className={`flex items-center justify-center gap-2 border border-dashed rounded-lg px-3 py-2 text-sm cursor-pointer transition-colors ${customerId ? "border-weld/40 text-weld-glow hover:bg-weld/10" : "border-white/10 text-white/30 pointer-events-none"}`}>
            <Upload size={14} /> {uploading ? "Uploading..." : "Choose photos"}
            <input data-testid="crm-photo-upload" type="file" accept="image/*" multiple className="hidden" onChange={upload} disabled={!customerId || uploading} />
          </span>
        </label>
      </div>

      {error && <Err error={error} />}
      {photos === null && customerId && <Loading />}
      {photos && photos.length === 0 && customerId && !error && (
        <p className="text-white/35 text-center py-10">No photos filed for this selection yet.</p>
      )}

      {photos && photos.length > 0 && (
        <>
          {befores.length > 0 && afters.length > 0 && (
            <div className="rounded-xl border border-white/10 p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-weld-glow text-xs uppercase tracking-wider">Before / After Compare</h4>
                {!compare && <Button size="sm" variant="outline" onClick={() => setCompare({ before: befores[0], after: afters[0] })}>Compare first pair</Button>}
              </div>
              {compare && (
                <BeforeAfter
                  before={compare.before["Website URL"]}
                  after={compare.after["Website URL"]}
                  altBefore={compare.before["Project Name"] || "Before"}
                  altAfter={compare.after["Project Name"] || "After"}
                  className="rounded-lg border border-weld/20"
                />
              )}
            </div>
          )}

          <div data-testid="crm-photo-grid" className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {photos.map(p => (
              <div key={p["Media ID"]} className="relative group rounded-lg overflow-hidden border border-white/10">
                <img src={p["Website URL"]} alt={p["Project Name"] || p["Media ID"]} className="w-full aspect-square object-cover" loading="lazy" />
                <div className="absolute inset-x-0 bottom-0 bg-black/70 backdrop-blur-sm px-2 py-1.5 flex items-center justify-between">
                  <Pill tone="blue">{p["Category"]}</Pill>
                  <span className="flex gap-1">
                    <a href={p["Drive URL"]} target="_blank" rel="noopener noreferrer" aria-label="Open in Drive"
                      className="p-1.5 text-white/60 hover:text-weld-glow transition-colors"><ExternalLink size={14} /></a>
                    <button data-testid={`photo-delete-${p["Media ID"]}`} onClick={() => remove(p["Media ID"])} aria-label="Delete photo"
                      className="p-1.5 text-white/60 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
                  </span>
                </div>
                <span className="absolute top-1 left-1"><Pill>{p["Media ID"]}</Pill></span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
